import React from 'react'
import ReactDOM from 'react-dom'
import { BLUE, inputStyle } from './tokens'

// These are re-exported for backward compat — all new imports should use ./tokens directly
export { BLUE, inputStyle } // eslint-disable-line react-refresh/only-export-components

// ─── SectionBlock ────────────────────────────────────────────────────────────
export function SectionBlock({ icon, title, subtitle, children }: {
  icon: React.ReactNode
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    <div style={{
      background: 'white', borderRadius: 10,
      border: '1px solid #e8eaed',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      // No `overflow: hidden` — it clips help tooltips/popovers that extend past
      // the card edge. Rounded corners are preserved by the radius on this element.
      marginBottom: 16,
    }}>
      {/* Card header */}
      <div style={{ padding: '16px 20px 14px', borderBottom: '1px solid #f3f4f6' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 8,
            border: '1px solid #e8eaed', background: '#f7f8fa',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, flexShrink: 0, color: '#6b7280',
          }}>
            {icon}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', lineHeight: 1.2 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{subtitle}</div>}
          </div>
        </div>
      </div>
      {/* Card body */}
      <div style={{ padding: '16px 20px' }}>
        {children}
      </div>
    </div>
  )
}

// ─── RadioPill ───────────────────────────────────────────────────────────────
export function RadioPill({ label, selected, onClick, badge, disabled }: {
  label: string; selected: boolean; onClick: () => void; badge?: string; disabled?: boolean
}) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled || undefined}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', borderRadius: 7, cursor: disabled ? 'not-allowed' : 'pointer',
        border: `1px solid ${selected ? BLUE : '#d1d5db'}`,
        background: disabled ? '#f9fafb' : selected ? '#eef2ff' : 'white',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
          border: `2px solid ${selected ? BLUE : '#d1d5db'}`,
          background: selected ? BLUE : 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} />}
        </div>
        <span style={{ fontSize: 14, fontWeight: 400, color: selected ? '#111827' : '#374151' }}>
          {label}
        </span>
        {badge && (
          <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 20, background: '#f3f4f6', color: '#6b7280' }}>
            {badge}
          </span>
        )}
      </div>
    </div>
  )
}

// ─── RadioPillGrid (2-column) ─────────────────────────────────────────────────
export function RadioPillGrid({ options, value, onChange }: {
  options: { label: string; value: string; badge?: string }[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      {options.map(opt => (
        <RadioPill key={opt.value} label={opt.label} badge={opt.badge}
          selected={value === opt.value}
          onClick={() => onChange(value === opt.value ? '' : opt.value)} />
      ))}
    </div>
  )
}

// ─── RadioPillList (vertical) ─────────────────────────────────────────────────
export function RadioPillList({ options, value, onChange }: {
  options: { label: string; value: string; badge?: string; disabled?: boolean }[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {options.map(opt => (
        <RadioPill key={opt.value} label={opt.label} badge={opt.badge} disabled={opt.disabled}
          selected={value === opt.value}
          onClick={() => onChange(value === opt.value ? '' : opt.value)} />
      ))}
    </div>
  )
}

// ─── Expandable ──────────────────────────────────────────────────────────────
export function Expandable({ open, children }: { open: boolean; children: React.ReactNode }) {
  if (!open) return null
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
      {children}
    </div>
  )
}

// ─── FieldRow ────────────────────────────────────────────────────────────────
export function FieldRow({ label, required, children, help }: {
  label: React.ReactNode; required?: boolean; children: React.ReactNode; help?: string
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
        <label style={{ fontSize: 12, fontWeight: 500, color: '#6b7280' }}>{label}</label>
        {required && <span style={{ fontSize: 11, color: '#dc2626', lineHeight: 1 }}>*</span>}
      </div>
      {children}
      {help && <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4, marginBottom: 0 }}>{help}</p>}
    </div>
  )
}

// ─── Toggle ──────────────────────────────────────────────────────────────────
export function Toggle({ on, onToggle, testId }: { on: boolean; onToggle: () => void; testId?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div data-testid={testId} onClick={onToggle} style={{
        width: 40, height: 22, borderRadius: 12, cursor: 'pointer', position: 'relative', flexShrink: 0,
        background: on ? BLUE : '#d1d5db', transition: 'background .15s',
      }}>
        <div style={{
          position: 'absolute', top: 3, left: on ? 21 : 3,
          width: 16, height: 16, borderRadius: '50%', background: 'white',
          transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.15)',
        }} />
      </div>
      <span style={{ fontSize: 13, color: '#6b7280' }}>{on ? 'Ja' : 'Nej'}</span>
    </div>
  )
}

// ─── Stepper ─────────────────────────────────────────────────────────────────
export function Stepper({ value, min = 0, max = 20, onChange }: {
  value: number; min?: number; max?: number; onChange: (v: number) => void
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      <button onClick={() => onChange(Math.max(min, value - 1))} style={{
        width: 36, height: 36, border: '1px solid #d1d5db', borderRadius: '7px 0 0 7px',
        background: 'white', cursor: 'pointer', fontSize: 16, color: '#374151',
      }}>−</button>
      <div style={{
        width: 56, height: 36, border: '1px solid #d1d5db', borderLeft: 'none', borderRight: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 500, color: '#111827',
      }}>{value}</div>
      <button onClick={() => onChange(Math.min(max, value + 1))} style={{
        width: 36, height: 36, border: '1px solid #d1d5db', borderRadius: '0 7px 7px 0',
        background: 'white', cursor: 'pointer', fontSize: 16, color: '#374151',
      }}>+</button>
    </div>
  )
}

// ─── SummaryRow ──────────────────────────────────────────────────────────────
export function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '11px 0', borderBottom: '1px solid #f3f4f6',
    }}>
      <span style={{ fontSize: 14, color: '#6b7280' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 500, color: value ? '#111827' : '#9ca3af' }}>
        {value || '—'}
      </span>
    </div>
  )
}

// ─── HelpTooltip ─────────────────────────────────────────────────────────────
// Clip-proof help popover. The bubble is rendered into a portal on document.body
// and positioned with `position: fixed` from the trigger's bounding rect, so no
// ancestor's `overflow` can cut it off.
export function HelpTooltip({ text }: { text: string }) {
  const ref = React.useRef<HTMLButtonElement>(null)
  const [open, setOpen] = React.useState(false)
  const [pos, setPos] = React.useState<{ left: number; top: number } | null>(null)

  const show = () => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    // Anchor the bubble just below the trigger, nudged left so it reads naturally.
    setPos({ left: Math.max(12, r.left - 12), top: r.bottom + 8 })
    setOpen(true)
  }
  const hide = () => setOpen(false)

  const bubbleWidth = Math.min(560, window.innerWidth - 24)
  // Keep the bubble fully on-screen horizontally.
  const left = pos ? Math.min(pos.left, window.innerWidth - bubbleWidth - 12) : 0

  return (
    <div style={{ position: 'relative', display: 'inline-flex', marginLeft: 6 }}>
      <button
        ref={ref}
        type="button"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        style={{
          width: 18, height: 18, borderRadius: '50%', border: '1.5px solid #d1d5db',
          background: open ? '#f0f6ff' : 'white', color: open ? BLUE : '#6b7280',
          fontSize: 11, fontWeight: 700, cursor: 'default', lineHeight: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >?</button>
      {open && pos && ReactDOM.createPortal(
        <div style={{
          position: 'fixed', left, top: pos.top, zIndex: 9999,
          background: '#1e293b', color: 'white', borderRadius: 10,
          padding: '14px 18px', fontSize: 12, lineHeight: 1.7,
          width: bubbleWidth, maxWidth: 560, minWidth: 260,
          boxShadow: '0 8px 24px rgba(0,0,0,.22)', pointerEvents: 'none',
        }}>
          {text}
        </div>,
        document.body,
      )}
    </div>
  )
}

// ─── LegalTip ────────────────────────────────────────────────────────────────
export function LegalTip({ title, text }: { title: string; text: string }) {
  return (
    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 3 }}>⚖ {title}</div>
      <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>{text}</div>
    </div>
  )
}

// ─── SubSectionDivider ───────────────────────────────────────────────────────
export function SubSectionDivider({ title }: { title?: string }) {
  return (
    <div style={{ borderTop: '1px solid #f3f4f6', marginTop: 16, paddingTop: 16, marginBottom: 12 }}>
      {title && (
        <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
          {title}
        </div>
      )}
    </div>
  )
}
